import { objectSpread2 as _objectSpread2, objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import { WINDOW_RESIZE_DEBOUNCE, useFocusWithin, mergeCssModules } from '@emoney/kyber-helpers';
import React__default, { useState, useRef, useEffect } from 'react';
import debounce$1 from 'lodash/debounce';
import { useAnalytics } from '@emoney/kyber-analytics';
import { keyboard, breakpoints } from '@emoney/utils';
import Portal$1 from '../Portal/Portal.js';
import Position$1 from '../Position/Position.js';
import '../Portal/index.js';
import '../Position/index.js';
import BasePopover$1 from './BasePopover/BasePopover.js';
import './BasePopover/index.js';
import defaultAnalytics from './popover.analytics.js';
import defaultCssModule from './less/popover.module.less.js';

var handleResizeDebounced = function handleResizeDebounced(setWindowWidth) {
  return debounce$1(function (event) {
    setWindowWidth(event.target.innerWidth);
  }, WINDOW_RESIZE_DEBOUNCE);
};

var Popover = function Popover(fromProps) {
  var _useAnalytics = useAnalytics(fromProps, Popover.defaultProps),
      trackableEventHandlers = _useAnalytics[1];

  var props = _objectSpread2(_objectSpread2({}, fromProps), trackableEventHandlers);

  var _useState = useState(window.innerWidth),
      windowWidth = _useState[0],
      setWindowWidth = _useState[1];

  var popoverRef = useRef(null);

  var handleBlur = function handleBlur(event) {
    if (shouldPreventBlur(event)) {
      popoverRef.current.focus();
      event.stopPropagation();
      return;
    }

    var autohide = props.autohide,
        show = props.show;

    if (autohide && show) {
      triggerCloseRequest(event);
    }
  };

  var _useFocusWithin = useFocusWithin({
    onBlurWithin: function onBlurWithin(event) {
      return handleBlur(event);
    }
  }),
      focusWithinProps = _useFocusWithin.focusWithinProps;

  useEffect(function () {
    if (props.show) {
      requestAnimationFrame(function () {
        var autofocusedElement = popoverRef.current && popoverRef.current.querySelector('[data-autofocus]');

        if (autofocusedElement) {
          autofocusedElement.focus();
        } else {
          popoverRef.current && popoverRef.current.focus();
        }
      });
      window.addEventListener('resize', handleResizeDebounced(setWindowWidth));
    } else {
      window.removeEventListener('resize', handleResizeDebounced(setWindowWidth));
    }
  }, [props.show]);
  useEffect(function () {
    return window.removeEventListener('resize', handleResizeDebounced(setWindowWidth)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var triggerCloseRequest = function triggerCloseRequest(event) {
    props.onRequestClose(event);
  };

  var shouldPreventBlur = function shouldPreventBlur(event) {
    // In a web component in Chrome, the event target is the custom element, so we are using the path
    var path = event.path || event.composedPath && event.composedPath(); // When a focused element becomes disabled Chrome moves the focus to the <body> causing the
    // Popover to close unless we prevent that here.
    // Non-Chrome browsers tend to return an empty composedPath

    if (path && path.length > 0 && path[0].disabled) {
      return true;
    }

    return false;
  };

  var handleKeyDown = function handleKeyDown(event) {
    if (keyboard.isEscape(event)) {
      triggerCloseRequest(event);
    }
  };

  var handleCloseClick = function handleCloseClick(event) {
    triggerCloseRequest(event);
  };

  var calculateMobileWidth = function calculateMobileWidth() {
    if (windowWidth <= breakpoints.MOBILE) {
      return {
        width: windowWidth + "px"
      };
    } else {
      return undefined;
    }
  };

  var calculateMobileOffset = function calculateMobileOffset() {
    if (windowWidth <= breakpoints.MOBILE) {
      if (!props.positionTo() || !popoverRef.current) {
        return props.offset;
      }

      var boundingRect = props.positionTo().getBoundingClientRect();
      var scrollLeft = (document.scrollingElement || document.documentElement).scrollLeft;
      var divisor = 2;
      var triggerOffset;

      switch (props.align) {
        case 'right':
          triggerOffset = boundingRect.left + boundingRect.width + scrollLeft;
          return [popoverRef.current.offsetWidth - triggerOffset, 0];

        case 'center':
          triggerOffset = boundingRect.left + boundingRect.width / divisor + scrollLeft;
          return [popoverRef.current.offsetWidth / divisor - triggerOffset, 0];

        case 'left':
          triggerOffset = boundingRect.left + scrollLeft;
          return [triggerOffset * -1, 0];

        default:
          return [0, 0];
      }
    }

    return props.offset;
  };

  var _saveRef = function saveRef(ref) {
    popoverRef.current = ref;
    props.innerRef(ref);
  };

  var analytics = props.analytics,
      children = props.children,
      labeledby = props.labeledby,
      align = props.align,
      show = props.show,
      positionTo = props.positionTo,
      innerRef = props.innerRef,
      _props$style = props.style,
      style = _props$style === void 0 ? {} : _props$style,
      autohide = props.autohide,
      onRequestClose = props.onRequestClose,
      flip = props.flip,
      cssModuleProp = props.cssModule,
      id = props.id,
      noPortal = props.noPortal,
      restProps = _objectWithoutPropertiesLoose(props, ["analytics", "children", "labeledby", "align", "show", "positionTo", "innerRef", "style", "autohide", "onRequestClose", "flip", "cssModule", "id", "noPortal"]);

  var cssModule = mergeCssModules(cssModuleProp, defaultCssModule);
  var placement = align === 'left' ? 'bottom-start' : align === 'right' ? 'bottom-end' : 'bottom';
  var popover = /*#__PURE__*/React__default.createElement(Position$1, {
    positionTo: positionTo,
    placement: placement,
    arrow: "." + cssModule['arrow'],
    offset: calculateMobileOffset(),
    flip: flip
  }, /*#__PURE__*/React__default.createElement(BasePopover$1, _extends({
    id: id,
    labeledby: labeledby,
    saveRef: function saveRef(ref) {
      _saveRef(ref);
    },
    containerStyle: calculateMobileWidth(),
    onKeyDown: handleKeyDown,
    onRequestClose: handleCloseClick,
    arrowClasses: cssModule['arrow'],
    style: style
  }, restProps, focusWithinProps), children));

  if (show) {
    return noPortal ? popover : /*#__PURE__*/React__default.createElement(Portal$1, {
      anchorNode: positionTo()
    }, popover);
  } else {
    return null;
  }
};

Popover.defaultProps = {
  autohide: true,
  show: false,
  align: 'center',
  innerRef: function innerRef() {},
  flip: false,
  noPortal: false,
  analytics: defaultAnalytics
};
Popover.displayName = 'Popover';

export default Popover;
